import { createWorldDraftId, getWorldDraftCatalog, getBeingRelationshipSignature } from './placeTreeWorldSave.js';

export const createEmptyWorldDevelopment = () => ({ nearbyPlaces: [], nearbyInhabitants: [], finalRecords: {}, relationships: [], socialOutlines: {}, availableRelateTypes: [], spatialDiagnostics: {} });
export const eventDraftSignature = events => JSON.stringify(events || []);
export const isDraftRecordSaved = (entry, progress) => Boolean(progress?.savedIds?.[entry.draftId] ||
  entry.entity.savedPlaceId || entry.entity.savedBeingId || entry.entity.savedItemId);
export const getRecordPreparationTasks = (draft, { enrichBeings = false } = {}) => {
  const signature = eventDraftSignature(draft.draftEvents);
  const finalRecords = draft.worldDevelopment?.finalRecords || {};
  const rootId = draft.draftPlace?.draftPlaceId || 'draft-place:root';
  return getWorldDraftCatalog(draft).filter(entry => {
    if (isDraftRecordSaved(entry, draft.worldSaveProgress)) return false;
    if (entry.kind === 'Place') return !enrichBeings && entry.draftId !== rootId && !finalRecords[entry.draftId];
    if (entry.kind === 'Being') return enrichBeings || entry.entity.identityOnly || (entry.entity.socialOutline && !finalRecords[entry.draftId]) ||
      (finalRecords[entry.draftId] && (finalRecords[entry.draftId].eventSignature !== signature ||
        (finalRecords[entry.draftId].relationshipSignature || '[]') !== getBeingRelationshipSignature(draft.worldDevelopment?.relationships, entry.draftId) ||
        (finalRecords[entry.draftId].socialSignature || 'null') !== JSON.stringify(entry.entity.socialOutline || null)));
    return !enrichBeings && !entry.entity.itemType;
  });
};

export const planSiteInhabitantsSerially = async ({ draft, development, api, onProgress, shouldStop = () => false,
  onStatus = () => {}, siteId }) => {
  let current = { ...development, nearbyPlaces: [...development.nearbyPlaces], nearbyInhabitants: [...development.nearbyInhabitants] };
  const names = new Set([...(draft.draftInhabitantSuggestions || []), ...current.nearbyInhabitants].map(being => being.name.trim().toLowerCase()));
  for (const site of current.nearbyPlaces.filter(entry => (!siteId || entry.draftPlaceId === siteId) && !entry.inhabitantsPlanned)) {
    if (shouldStop()) return current;
    onStatus(`Planning inhabitants of ${site.name}...`);
    const response = await api.planSiteInhabitants({ site, prompt: draft.sourcePrompt || draft.prompt,
      existingBeings: [...(draft.draftInhabitantSuggestions || []), ...current.nearbyInhabitants].map(being => ({ name: being.name, title: being.title })), aogYear: 21000 });
    if (!Array.isArray(response.inhabitants) || response.inhabitants.length !== site.plannedInhabitantCount) throw new Error(`The ${site.name} inhabitant batch did not match its reviewed plan.`);
    const inhabitants = response.inhabitants.map(being => ({ ...being, draftBeingId: createWorldDraftId('nearby-being'),
      identityOnly: true, memberOfPlaceId: site.draftPlaceId, populationTranche: 'nearby' }));
    for (const being of inhabitants) {
      const name = being.name.trim().toLowerCase();
      if (names.has(name)) throw new Error(`Duplicate nearby inhabitant name: ${being.name}`);
      names.add(name);
    }
    current = { ...current, nearbyInhabitants: [...current.nearbyInhabitants, ...inhabitants],
      nearbyPlaces: current.nearbyPlaces.map(entry => entry.draftPlaceId === site.draftPlaceId ? { ...entry, inhabitantsPlanned: true } : entry) };
    onProgress(current);
  }
  return current;
};

export const prepareWorldRecordsSerially = async ({ draft, development, api, onProgress, onPrepared = () => {},
  shouldStop = () => false, onStatus = () => {}, enrichBeings = false, nextOnly = false, onlyDraftIds }) => {
  let current = { ...development, finalRecords: { ...development.finalRecords } };
  const signature = eventDraftSignature(draft.draftEvents);
  if (current.nearbyPlaces.some(site => site.plannedInhabitantCount > 0 && !site.inhabitantsPlanned &&
    (!onlyDraftIds || onlyDraftIds.includes(site.draftPlaceId))) && !enrichBeings) throw new Error('Plan nearby inhabitants before preparing final records.');
  const tasks = getRecordPreparationTasks({ ...draft, worldDevelopment: current }, { enrichBeings })
    .filter(entry => !onlyDraftIds || onlyDraftIds.includes(entry.draftId));
  const rootId = draft.draftPlace.draftPlaceId || 'draft-place:root';
  const preparedPlaces = () => getWorldDraftCatalog({ ...draft, worldDevelopment: current })
    .filter(entry => entry.kind === 'Place' && (entry.draftId === rootId || current.finalRecords[entry.draftId] || isDraftRecordSaved(entry, draft.worldSaveProgress)));
  const remaining = [...tasks];
  let completed = 0;
  while (remaining.length && !shouldStop()) {
    const places = preparedPlaces();
    const index = remaining.findIndex(entry => entry.kind !== 'Place' || entry.entity.isNearby || !entry.entity.parentSuggestionName ||
      places.some(place => place.entity.name.trim().toLowerCase() === entry.entity.parentSuggestionName.trim().toLowerCase()));
    if (index < 0) throw new Error('A parent Place must be prepared before its child. Check the sub-place hierarchy.');
    const entry = remaining.splice(index, 1)[0];
    const identity = { ...entry.entity };
    if (entry.kind === 'Being' && !places.some(place => place.draftId === identity.memberOfPlaceId)) throw new Error(`Prepare the home of ${identity.name} before generating the Being.`);
    onStatus(`Preparing ${entry.kind}: ${identity.name} (${completed + 1} of ${tasks.length})...`);
    const parent = entry.kind === 'Place' && !identity.isNearby
      ? identity.parentSuggestionName ? places.find(place => place.entity.name.trim().toLowerCase() === identity.parentSuggestionName.trim().toLowerCase()) : places.find(place => place.draftId === rootId)
      : null;
    let response;
    try {
      response = await api.generateFinal({ kind: entry.kind, draftId: entry.draftId, identity,
      ...(entry.kind === 'Place' ? { placementMode: identity.isNearby ? 'nearby' : 'child' } : {}),
      catalog: getWorldDraftCatalog({ ...draft, worldDevelopment: current }).map(record => ({ kind: record.kind, draftId: record.draftId,
        name: record.entity.name, title: record.entity.title, placeType: record.entity.placeType,
        description: record.entity.description, memberOfPlaceId: record.entity.memberOfPlaceId, lifeStage: record.entity.lifeStage,
        ownerDraftBeingId: record.entity.ownerDraftBeingId, itemType: record.entity.itemType })),
      rootPlace: draft.draftPlace, regionPlace: current.regionPlace,
      existingPlaces: [...places.map(place => ({ ...place.entity, draftPlaceId: place.draftId })), ...(current.savedNearbyPlaces || [])],
      parentPlace: parent ? { ...parent.entity, idEntity: draft.worldSaveProgress?.savedIds?.[parent.draftId] || parent.entity.savedPlaceId || '',
        composedOfChildren: places.filter(place => place.draftId !== parent.draftId && !place.entity.isNearby &&
          (place.entity.parentSuggestionName ? place.entity.parentSuggestionName === parent.entity.name : parent.draftId === rootId)
        ).map(place => place.entity) } : null,
      events: draft.draftEvents, relationships: current.relationships || [], prompt: draft.sourcePrompt || draft.prompt, aogYear: 21000 });
      if (!response?.record) throw new Error(`No final ${entry.kind} record returned.`);
    } catch (error) {
      const message = `${identity.name} (${entry.kind}): ${error.message || String(error)}`;
      if (entry.kind === 'Place') {
        const diagnostics = error.spatialDiagnostics || { name: identity.name, attempts: [{ stage: 'generation', reason: message }] };
        current = { ...current, spatialDiagnostics: { ...current.spatialDiagnostics, [entry.draftId]: diagnostics } };
        onProgress(current);
      }
      throw Object.assign(new Error(message), { code: error.code, spatialDiagnostics: error.spatialDiagnostics });
    }
    current = { ...current,
      ...(entry.kind === 'Place' && identity.isNearby && response.nearbyPlan ? {
        nearbyPlaces: current.nearbyPlaces.map(site => site.draftPlaceId === entry.draftId ? { ...site,
          mapReferences: response.nearbyPlan.mapReferences, sourceRequirements: response.nearbyPlan.sourceRequirements,
          rangeAdjustments: response.nearbyPlan.rangeAdjustments } : site)
      } : {}),
      ...(response.savedNearbyPlaces ? { savedNearbyPlaces: response.savedNearbyPlaces } : {}),
      ...(response.spatialDiagnostics ? { spatialDiagnostics: { ...current.spatialDiagnostics, [entry.draftId]: response.spatialDiagnostics } } : {}),
      finalRecords: { ...current.finalRecords, [entry.draftId]: {
      record: { ...identity, ...response.record, ...(entry.kind === 'Place' ? { isNearby: identity.isNearby } : {}),
        identityOnly: false, finalRecord: true }, eventSignature: signature,
      relationshipSignature: getBeingRelationshipSignature(current.relationships, entry.draftId), socialSignature: JSON.stringify(identity.socialOutline || null)
    } } };
    onProgress(current);
    onPrepared(entry, current.finalRecords[entry.draftId].record);
    completed += 1;
    if (nextOnly) break;
  }
  return { development: current, completed, remaining: tasks.length - completed, stopped: shouldStop() };
};

export const getPreparedPlaceRetryIds = (draft, development, selectedIds) => {
  const rootId = draft.draftPlace?.draftPlaceId || 'draft-place:root';
  const places = getWorldDraftCatalog({ ...draft, worldDevelopment: development }).filter(entry => entry.kind === 'Place' && entry.draftId !== rootId);
  const selected = new Set(selectedIds || places.filter(entry => development.finalRecords?.[entry.draftId] && !isDraftRecordSaved(entry, draft.worldSaveProgress)).map(entry => entry.draftId));
  // Moving a compound also invalidates the prepared footprints of its children.
  let changed = true;
  while (changed) {
    changed = false;
    const names = new Set(places.filter(entry => selected.has(entry.draftId)).map(entry => entry.entity.name.trim().toLowerCase()));
    for (const entry of places) {
      if (!selected.has(entry.draftId) && (development.finalRecords?.[entry.draftId] || isDraftRecordSaved(entry, draft.worldSaveProgress)) &&
        entry.entity.parentSuggestionName && names.has(entry.entity.parentSuggestionName.trim().toLowerCase())) {
        selected.add(entry.draftId); changed = true;
      }
    }
  }
  const entries = places.filter(entry => selected.has(entry.draftId));
  const saved = entries.find(entry => isDraftRecordSaved(entry, draft.worldSaveProgress));
  if (saved) throw new Error(`${saved.entity.name} is already saved. Edit saved Places in the Place Editor.`);
  return entries.map(entry => entry.draftId);
};

export const retryPreparedPlacesSerially = async options => {
  if (options.shouldStop?.()) return { development: options.development, completed: 0, remaining: 0, stopped: true };
  const ids = getPreparedPlaceRetryIds(options.draft, options.development, options.selectedIds);
  const finalRecords = { ...options.development.finalRecords };
  const spatialDiagnostics = { ...options.development.spatialDiagnostics };
  for (const id of ids) { delete finalRecords[id]; delete spatialDiagnostics[id]; }
  const development = { ...options.development, finalRecords, spatialDiagnostics };
  options.onProgress(development);
  return prepareWorldRecordsSerially({ ...options, development, onlyDraftIds: ids });
};
